import {
  createSessionChatLog,
  getSourcedFromDocumentNames,
  type ChatLogMessageItem,
} from './api';
import { shouldHideAssistantSourceLine } from '../utils/chatLogSource';

// LangChain-compatible chat history interface
export interface BaseChatMessageHistory {
  getMessages(): Promise<ChatLogMessageItem[]>;
  addMessage(message: ChatLogMessageItem): Promise<void>;
  addMessages(messages: ChatLogMessageItem[]): Promise<void>;
  clear(): Promise<void>;
}

// In-memory chat history implementation
export class InMemoryChatMessageHistory implements BaseChatMessageHistory {
  private messages: ChatLogMessageItem[] = [];

  async getMessages(): Promise<ChatLogMessageItem[]> {
    return [...this.messages];
  }

  async addMessage(message: ChatLogMessageItem): Promise<void> {
    this.messages.push(message);
  }

  async addMessages(messages: ChatLogMessageItem[]): Promise<void> {
    this.messages.push(...messages);
  }

  async clear(): Promise<void> {
    this.messages = [];
  }
}

// Chat history manager with automatic admin logging
export class ChatHistoryManager {
  private histories: Map<string, InMemoryChatMessageHistory> = new Map();
  private currentSessionId: string | null = null;
  private currentUser: { email: string; role: string; full_name?: string } | null = null;
  private savedSessions: Set<string> = new Set(); // Track which sessions have been saved to admin logs
  // For incremental saves: remember how many messages were last saved per session
  private savedMessageCounts: Map<string, number> = new Map();
  private savePromises: Map<string, Promise<void>> = new Map();

  constructor() {
    // Auto-save to admin logs when session ends (page unload, new chat, etc.)
    window.addEventListener('beforeunload', () => {
      if (this.currentSessionId && !this.savedSessions.has(this.currentSessionId)) {
        void this.saveSessionToAdminLogs(this.currentSessionId);
      }
    });
  }

  setCurrentUser(user: { email: string; role: string; full_name?: string }) {
    this.currentUser = user;
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  getChatHistory(sessionId: string): InMemoryChatMessageHistory {
    if (!this.histories.has(sessionId)) {
      this.histories.set(sessionId, new InMemoryChatMessageHistory());
    }
    return this.histories.get(sessionId)!;
  }

  setCurrentSession(sessionId: string, savePrevious: boolean = true) {
    if (savePrevious && this.currentSessionId && this.currentSessionId !== sessionId) {
      void this.saveSessionToAdminLogs(this.currentSessionId);
    }
    this.currentSessionId = sessionId;
  }

  /**
   * Align in-memory history with chat_sessions storage.
   * Does NOT update savedMessageCounts — that counter tracks admin chat_logs only.
   */
  async syncSessionFromServer(sessionId: string, messages: ChatLogMessageItem[]): Promise<void> {
    const history = this.getChatHistory(sessionId);
    const local = await history.getMessages();

    if (local.length === 0 && messages.length > 0) {
      await history.addMessages(messages);
    } else if (messages.length > local.length) {
      await history.clear();
      await history.addMessages(messages);
    }

    this.setCurrentSession(sessionId, false);
  }

  async addMessage(sessionId: string, message: ChatLogMessageItem): Promise<void> {
    const history = this.getChatHistory(sessionId);
    await history.addMessage(message);
    this.setCurrentSession(sessionId, false);
  }

  async addMessages(sessionId: string, messages: ChatLogMessageItem[]): Promise<void> {
    const history = this.getChatHistory(sessionId);
    await history.addMessages(messages);
    this.setCurrentSession(sessionId, false);
  }

  async getMessages(sessionId: string): Promise<ChatLogMessageItem[]> {
    const history = this.getChatHistory(sessionId);
    return await history.getMessages();
  }

  async clearSession(sessionId: string): Promise<void> {
    const history = this.getChatHistory(sessionId);
    await history.clear();
  }

  // Extract SOP references from messages (including sopSource data)
  private extractSopReferences(messages: ChatLogMessageItem[]): string[] {
    const sopRefs = new Set<string>();

    const legitimateMessages = messages.filter(
      (m) => m.role !== 'assistant' || !shouldHideAssistantSourceLine(m.text || ''),
    );

    const allText = legitimateMessages.map(m => m.text || '').join(' ');
    const sopPattern = /\bSOP-[A-Z]{2,}-\d{2,4}\b/gi;
    const textMatches = allText.match(sopPattern) || [];
    textMatches.forEach(match => sopRefs.add(match.toUpperCase()));

    legitimateMessages.forEach(message => {
      if (message.sopSource?.sopName) {
        sopRefs.add(message.sopSource.sopName);
      }
      getSourcedFromDocumentNames(message.sourcedFrom).forEach((name) =>
        sopRefs.add(name),
      );
    });

    return Array.from(sopRefs);
  }

  // Generate title from first user message
  private generateTitle(messages: ChatLogMessageItem[]): string {
    const firstUserMessage = messages.find(m => m.role === 'user');
    if (!firstUserMessage) return 'Untitled Chat';

    const text = firstUserMessage.text.trim();
    return text.length > 60 ? text.substring(0, 57) + '...' : text;
  }

  private async appendNewMessagesToAdminLogs(sessionId: string): Promise<void> {
    if (!this.currentUser) return;

    const messages = await this.getMessages(sessionId);
    if (messages.length === 0) return;

    const lastSavedCount = this.savedMessageCounts.get(sessionId) || 0;
    if (messages.length <= lastSavedCount) {
      return;
    }

    const newMessages = messages.slice(lastSavedCount);
    const sopReferences = this.extractSopReferences(messages);
    const title = this.generateTitle(messages);

    await createSessionChatLog({
      title,
      sop_referenced: sopReferences.length ? sopReferences.join(', ') : '-',
      messages: newMessages.map(m => ({
        role: m.role,
        text: m.text,
        images: m.images || [],
        timestamp: m.timestamp,
        messageId: m.messageId,
        sopSource: m.sopSource,
        sourcedFrom: m.sourcedFrom,
      })),
      session_id: sessionId,
    });

    this.savedMessageCounts.set(sessionId, messages.length);
    this.savedSessions.add(sessionId);
  }

  async saveSessionToAdminLogs(sessionId: string): Promise<void> {
    const inFlight = this.savePromises.get(sessionId);
    if (inFlight) {
      await inFlight;
      return;
    }

    const originalSessionId = this.currentSessionId;
    this.currentSessionId = sessionId;

    const savePromise = (async () => {
      try {
        await this.appendNewMessagesToAdminLogs(sessionId);
      } catch {
        // Ignore admin log failures; chat session storage remains source of truth
      } finally {
        this.currentSessionId = originalSessionId;
        this.savePromises.delete(sessionId);
      }
    })();

    this.savePromises.set(sessionId, savePromise);
    await savePromise;
  }

  // Save completed manager chat session to admin logs (incremental pair append only)
  async saveCompletedManagerSession(sessionId: string): Promise<void> {
    await this.saveSessionToAdminLogs(sessionId);
  }

  // Start new chat (clears current session)
  startNewChat(): string {
    const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.setCurrentSession(newSessionId);
    return newSessionId;
  }

  clearSavedSessions(): void {
    this.savedSessions.clear();
  }
}

export const chatHistoryManager = new ChatHistoryManager();
